<script>
import Common from '../../../common/Common.vue'
import Services from '../../../common/Services.vue'
import { applySessionScope } from '../../../utils/scope'
import { clampTimelineRange, cleanTimelineRow, parseTimelineFilterParams } from '../../../utils/timeline'
import moment from 'moment-timezone'

export default {
    init: async payload => {
        await payload.initRangeDate()
        // garantir locale igual ao GeneralSummary
        payload.userLocale = payload.$i18n.locale === 'pt' ? 'pt-br' : 'en'
        const role = await Common.getSubtype(payload)
        payload.role = role

        payload.isSuperAdminMaster = await Common.isSuperAdminMaster(payload)

        await payload.initTable()

        if (!payload.isSuperAdminMaster || role === 'AUDITOR') {
            payload.columns.splice(5, 1)
        }

        if (role === 'SUPER_ADMIN_MASTER') {
            payload.accounts = await Services.getAccounts(payload)
        } else if (role === 'ADMIN' || role === 'MANAGER') {
            payload.clients = await Services.getClients(payload)
        } else if (role === 'MANAGER' || role === 'OPERATOR' || role === 'AUDITOR') {
            const client = await Common.getClientId(payload)
            payload.filters.client = client
            payload.sites = await Services.getSites(payload)
        }
    },
    methods: {
        async filter() {

            if (this.isLoading) return


            if (this.role === 'SUPER_ADMIN_MASTER' && !this.filters.account) {
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.select.account.required'))
                return
            }

            if (!applySessionScope(this, this.filters)) return

            this.isLoading = true

            try {
                const page = await Services.getFreePatrolsPage(this, this.filters)
                this.items = page.results
                this.totalItems = page.totalCount
            } catch (_error) {
                this.items = []
                this.totalItems = 0
                Common.show(this, 'top-right', 'danger', this.$t('str.error_loading_data'))
            } finally {
                this.isLoading = false
            }
        },
        filterFromFirstPage() {
            this.currentPage = 1
            this.filters.skip = 1
            this.filter()
        },
        onPageChange(newPage) {
            this.currentPage = newPage.currentPage
            this.filters.skip = this.currentPage
            this.filter()
        },
        onPerPageChange(newPerPage) {
            this.paginationOptions.perPage = newPerPage.currentPerPage // Atualize a opção de páginação
            this.filters.limit = newPerPage.currentPerPage // Atualize o limite nos filtros
            this.filters.skip = 1 // O backend recebe número de página, começando em 1.
            this.currentPage = 1 // Volte para a primeira página
            this.filter() // Atualize os dados
        },
        async initTable() {
            this.columns = [
                {
                    label: this.$t('str.table.timeline.column.vigilant'),
                    field: 'vigilant',
                    width: '10%',
                    sortable: true,
                    firstSortType: 'desc',
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.starts.in'),
                    field: 'startDate',
                    width: '10%',
                    sortable: true,
                    tdClass: 'text-nowrap',
                    thClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.ends.in'),
                    field: 'endDate',
                    width: '10%',
                    sortable: true,
                    tdClass: 'text-nowrap',
                    thClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.status'),
                    field: 'status',
                    width: '10%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.account'),
                    field: 'account.name',
                    width: '10%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.client'),
                    field: 'client.name',
                    width: '10%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.table.timeline.column.site'),
                    field: 'site.name',
                    width: '10%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
            ]

            this.paginationOptions = {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                position: 'bottom',
                perPageDropdown: [10, 20, 50, 100],
                dropdownAllowAll: false,
                setCurrentPage: 1,
                jumpFirstOrLast: true,
                firstLabel: this.$t('str.table.pagination.first.page'),
                lastLabel: this.$t('str.table.pagination.last.page'),
                nextLabel: this.$t('str.table.pagination.next.page'),
                prevLabel: this.$t('str.table.pagination.prev.page'),
                rowsPerPageLabel: this.$t('str.table.pagination.rows.per.page.lavel'),
                ofLabel: this.$t('str.table.pagination.of.label.page'),
                pageLabel: this.$t('str.table.pagination.page'),
                allLabel: this.$t('str.table.pagination.all.label'),
            }
        },
        initRangeDate: async function () {
            const accountTimezone = Common.getAccountTimezone()
            const startDate = moment().tz(accountTimezone).subtract(0, 'days')
            const endDate = moment().tz(accountTimezone)
            const today = moment().tz(accountTimezone)
            const yesterday = moment().tz(accountTimezone).subtract(1, 'days')
            const thisMonthStart = moment().tz(accountTimezone).startOf('month')

            this.dateRange = {
                opens: 'right',
                singleDatePicker: false,
                timePicker: false,
                timePicker24Hour: false,
                showWeekNumbers: false,
                showDropdowns: true,
                autoApply: false,
                linkedCalendars: true,
                closeOnEsc: true,
                range: {
                    startDate: startDate,
                    endDate: endDate,
                },
                maxDate: moment().tz(accountTimezone).endOf('day').toDate(),
                sampleLocaleData: {
                    direction: 'ltr',
                    format: this?.userLocale === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy',
                    separator: ' - ',
                    applyLabel: this.$t('str.apply'),
                    cancelLabel: this.$t('str.cancel'),
                    weekLabel: 'W',
                    ranges: {
                        [this.$t('str.date.today')]: [today, today],
                        [this.$t('str.date.yesterday')]: [yesterday, yesterday],
                        [this.$t('str.date.this.month')]: [thisMonthStart, today],
                        [this.$t('str.date.last.month')]: [moment().tz(accountTimezone).subtract(1, 'month').startOf('month'), moment().tz(accountTimezone).subtract(1, 'month').endOf('month')],
                    },
                    daysOfWeek: [
                        this.$t('str.abbreviation.sunday'),
                        this.$t('str.abbreviation.monday'),
                        this.$t('str.abbreviation.tuesday'),
                        this.$t('str.abbreviation.wednesday'),
                        this.$t('str.abbreviation.thursday'),
                        this.$t('str.abbreviation.friday'),
                        this.$t('str.abbreviation.saturday'),
                    ],
                    monthNames: [
                        this.$t('str.abbreviation.january'),
                        this.$t('str.abbreviation.february'),
                        this.$t('str.abbreviation.march'),
                        this.$t('str.abbreviation.april'),
                        this.$t('str.abbreviation.may'),
                        this.$t('str.abbreviation.june'),
                        this.$t('str.abbreviation.july'),
                        this.$t('str.abbreviation.august'),
                        this.$t('str.abbreviation.september'),
                        this.$t('str.abbreviation.october'),
                        this.$t('str.abbreviation.november'),
                        this.$t('str.abbreviation.december'),
                    ],
                    firstDay: 0,
                },
            }
        },
        updateRangeDate: function (start, end) {
            const accountTimezone = Common.getAccountTimezone()
            const startDate = moment(start).tz(accountTimezone).subtract(0, 'days')
            const endDate = moment(end).tz(accountTimezone)

            this.dateRange.range = {
                startDate: startDate,
                endDate: endDate,
            }
        },
        selectItem(params) {
            this.selectedItem = cleanTimelineRow(params.row)
            this.$bvModal.show('infoItemModal')
        },
        applyRouteFilters() {
            const routeParams = parseTimelineFilterParams(this.filterParams)
            if (!routeParams) return

            const accountTimezone = Common.getAccountTimezone()
            const { startDate, endDate, wasClamped } = clampTimelineRange(routeParams.startDate, routeParams.endDate, accountTimezone)
            if (wasClamped) Common.show(this, 'top-right', 'warn', this.$t('str.charts.date.range.max'))

            const filters = {
                ...this.filters,
                ...routeParams,
                account: routeParams.account || '',
                client: routeParams.client || '',
                site: routeParams.site || '',
                isDescSortByStartDate: false,
                isSortByStartDate: true,
                startDate,
                endDate,
            }

            this.filters = filters
            this.updateRangeDate(filters.startDate, filters.endDate)
        },
        changeAccount: async function () {
            const account = this.filters.account

            if (account === '') {
                this.filters.client = ''
                this.filters.site = ''
            }
            this.clients = await Services.getClientsByAccount(this, account)
        },
        changeClient: async function () {
            const client = this.filters.client

            if (client === '') {
                this.filters.site = ''
            }

            this.sites = await Services.getSitesByClient(this, client)
        },
        changeSite: async function () {
        },
        async updateValues(d) {
            const accountTimezone = Common.getAccountTimezone()
            const { startDate, endDate, wasClamped } = clampTimelineRange(d.startDate, d.endDate, accountTimezone)

            if (wasClamped) {
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.date.range.max'))
            }

            this.dateRange.range.startDate = startDate
            this.dateRange.range.endDate = endDate
            this.filters.startDate = startDate
            this.filters.endDate = endDate
        },
        getStatusName: Common.getEventStatusName,
        formatDate: Common.formatDateAndTime,
    },
}
</script>
